import { _callBackend } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { LinkedAccount } from '@/_types/models/linkedAccount';
import type { User } from '@/_types/models/user';

export const removeUserEmail = async ({
  userId,
  token,
}: APIRequest<{
  userId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/users/assign_temporary_email',
    'POST',
    {
      user_id: userId,
    },
    token,
  );

export const setSignInMethod = async ({
  signInMethod,
  newPassword,
  phoneNumber,
  email,
  token,
}: APIRequest<{
  signInMethod: string;
  newPassword?: string;
  phoneNumber?: string;
  email?: string;
}>): APIResponsePromise<{ firebase_token: string }> =>
  await _callBackend(
    '/users/set_sign_in_method',
    'POST',
    {
      sign_in_method: signInMethod,
      new_password: newPassword,
      phone_number: phoneNumber,
      email,
    },
    token,
  );

export const changeLoginMethod = async ({
  uid,
  username,
  redirect,
}: APIRequest<{
  uid?: string;
  username: string;
  redirect?: string | null;
}>): APIResponsePromise =>
  await _callBackend(
    '/users/change_login_method',
    'POST',
    {
      uid,
      username,
      redirect,
    },
    undefined,
    {
      returnResponseAsIs: true,
    },
  );

export const updateChangelogLastReviewedAt = async ({
  userId,
  token,
}: APIRequest<{
  userId: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/users/${userId}`,
    'PATCH',
    { change_log_last_reviewed_at: new Date().toISOString() },
    token,
  );

export interface LinkedAccountsResponse {
  accounts: LinkedAccount[];
}

export const getLinkedAccounts = async ({
  token,
}: APIRequest): APIResponsePromise<LinkedAccountsResponse> =>
  await _callBackend('/users/get_linked_accounts', 'GET', null, token);

export const linkAccount = async ({
  provider,
  code,
  token,
}: APIRequest<{
  provider: string;
  code: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/users/link_account',
    'POST',
    {
      provider,
      code,
      redirect_uri: globalThis.location.origin + '/admin-profile', // required by Microsoft
    },
    token,
  );

export const unlinkAccount = async ({
  email,
  provider,
  token,
}: APIRequest<{
  email: string;
  provider: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/users/unlink_account',
    'POST',
    { email, provider },
    token,
  );

export interface UpdateUserAttrs {
  email?: string;
  fname?: string;
  group_id?: number | null;
  is_activating_all_entities?: boolean;
  is_deactivating_all_entities?: boolean;
  lname?: string;
  preferred_contact_method?: string | null;
  preferred_name?: string;
  external_user_id?: string;
  paying?: boolean;
  phone_number?: string;
  time_zone?: string;
  work_phone_extension?: string;
  work_phone_number?: string;
  work_email?: string;
  enabled_2fa?: boolean;
  userId: number;
  readonly_access?: boolean;
  assigned_advisor_id?: number;
  preferences?: {
    hide_conversation_in_email?: boolean;
  };
}

export const updateUser = async ({
  userId,
  email,
  fname,
  lname,
  preferred_contact_method,
  preferred_name,
  external_user_id,
  phone_number,
  time_zone,
  work_email,
  work_phone_number,
  group_id,
  work_phone_extension,
  enabled_2fa,
  paying,
  readonly_access,
  assigned_advisor_id,
  is_activating_all_entities,
  is_deactivating_all_entities,
  preferences,
  token,
}: APIRequest<UpdateUserAttrs>): APIResponsePromise<User> =>
  await _callBackend(
    `/users/${userId}`,
    'PATCH',
    {
      email,
      enabled_2fa,
      fname,
      group_id,
      is_activating_all_entities,
      is_deactivating_all_entities,
      lname,
      phone_number,
      external_user_id,
      preferred_contact_method,
      preferred_name,
      time_zone,
      work_email,
      work_phone_extension,
      work_phone_number,
      paying,
      readonly_access,
      assigned_advisor_id,
      preferences,
    },
    token,
  );
